import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useEnvironments, useSoilMoistures, useSoilTypesAndIrrigationChoices } from '/@/hooks/recommendation';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import { useHybrids } from '../hybrids/use-hybrids';
import { keepsEnvironment } from './planting-date-impact';

export type PlantingDateImpact = 'checking' | 'keeps' | 'changes';

/**
 * Runs the environment cascade's queries for a candidate planting date against the stored crop
 * config, without writing anything: `keeps` means applying that date leaves the environment and
 * the chosen hybrids as they are, so the wizard needn't send the user back to reconfigure them.
 *
 * The queries are enabled under the same conditions as in `useRecommendationEditData`, so a list
 * the cascade wouldn't fetch isn't checked either. Unset `plantingDate` runs nothing.
 */
export const usePlantingDateImpact = ({
    field,
    crop = 'corn',
    plantingDate,
}: {
    field?: Field;
    crop?: 'corn' | 'soy';
    plantingDate?: string;
}): PlantingDateImpact | undefined => {
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const enabled = !!plantingDate && !!cropConfig;

    const location = { lat: field?.lat, lng: field?.lng, countryCode: field?.country?.code };
    // These two hooks are untyped JS whose `plantingDate = undefined` default infers the param as
    // `undefined`; the casts only widen it back to the string the API takes.
    const hasSoilMoisture = cropConfig?.soilMoisture != null;

    const enableSoilMoistures = enabled && crop === 'corn' && field?.country?.code !== 'MX';
    const soilMoisturesQuery = useSoilMoistures(
        { ...location, climatePattern: cropConfig?.climatePattern, plantingDate } as Parameters<
            typeof useSoilMoistures
        >[0],
        { enabled: enableSoilMoistures }
    );

    const enableSoilTypes = enabled && crop === 'corn' && (!enableSoilMoistures || hasSoilMoisture);
    const soilTypesQuery = useSoilTypesAndIrrigationChoices(
        {
            ...location,
            climatePattern: cropConfig?.climatePattern,
            soilMoisture: cropConfig?.soilMoisture,
            plantingDate,
        } as Parameters<typeof useSoilTypesAndIrrigationChoices>[0],
        { enabled: enableSoilTypes }
    );

    const enableEnvironments = enabled && (crop === 'soy' || hasSoilMoisture);
    const environmentsQuery = useEnvironments(
        {
            ...location,
            climatePattern: cropConfig?.climatePattern,
            soilMoisture: cropConfig?.soilMoisture,
            crop: cropConfig?.crop,
            plantingDate,
            soilType: cropConfig?.soilType,
            irrigation: cropConfig?.irrigation,
        } as Parameters<typeof useEnvironments>[0],
        { enabled: enableEnvironments }
    );

    // Ranked for the candidate date at the stored environment: the environment only stays put
    // when the check passes, and then this is the list the hybrids step would show.
    const hybrids = useHybrids({ field, crop, plantingDate });
    const offeredHybridIds = useMemo(
        () => [...hybrids.featuredProducts, ...hybrids.otherProducts].map((hybrid) => hybrid.id),
        [hybrids.featuredProducts, hybrids.otherProducts]
    );

    if (!enabled) return undefined;

    const checking =
        soilMoisturesQuery.isLoading || soilTypesQuery.isLoading || environmentsQuery.isLoading || hybrids.isLoading;
    if (checking) return 'checking';

    return keepsEnvironment({
        config: cropConfig,
        soilMoistures: enableSoilMoistures ? soilMoisturesQuery.data : undefined,
        soilTypes: enableSoilTypes ? soilTypesQuery.data : undefined,
        environments: enableEnvironments ? environmentsQuery.data : undefined,
        offeredHybridIds,
    })
        ? 'keeps'
        : 'changes';
};
